import { useState } from 'react'
import { toast } from 'sonner'
import { useLoginByEmailMutation, useLoginWithOAuthMutation } from '../idApi'
import { useLanguage } from '@/hooks/useLanguage'
import { isValidEmail } from '@/lib/email'
import { LanguageToggle } from '@/components/common/LanguageToggle'
import { authT } from '../locale'
import { AuthEmailForm } from './auth/AuthEmailForm'
import { AuthLinkSentNotice } from './auth/AuthLinkSentNotice'
import { AuthIllustration } from './auth/AuthIllustration'

export const Auth = () => {
  const { language } = useLanguage()
  const l = authT[language]
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [accountNotFound, setAccountNotFound] = useState(false)
  const [linkSent, setLinkSent] = useState(false)
  const [loginByEmail, { isLoading }] = useLoginByEmailMutation()
  const [loginWithOAuth, { isLoading: isGoogleLoading }] = useLoginWithOAuthMutation()

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAccountNotFound(false)
    if (!isValidEmail(email)) {
      setEmailError(l.invalidEmail)
      return
    }
    setEmailError(null)
    try {
      await loginByEmail({
        email,
        redirect_url: window.location.origin,
      }).unwrap()
      ;(document.activeElement as HTMLElement | null)?.blur()
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setLinkSent(true)
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code
      if (code === 'user_not_found') {
        setAccountNotFound(true)
        return
      }
      toast.error(l.errEmailSend)
    }
  }

  const handleGoogle = async () => {
    try {
      const res = await loginWithOAuth({
        oauth: 'GOOGLE',
        redirect_url: window.location.origin,
      }).unwrap()
      if (!res.oauthRedirectTo) {
        toast.error(l.errGoogleUrl)
        return
      }
      // Reject any non-http(s) scheme — guards against javascript:/data: XSS
      // coming from a compromised or misconfigured backend response.
      let parsed: URL
      try {
        parsed = new URL(res.oauthRedirectTo)
      } catch {
        toast.error(l.errGoogleUrlInvalid)
        return
      }
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        toast.error(l.errGoogleUrlInvalid)
        return
      }
      window.location.href = parsed.toString()
    } catch {
      toast.error(l.errGoogleGeneric)
    }
  }

  return (
    <main className="min-h-dvh flex flex-col lg:flex-row bg-background relative">
      <div className="absolute top-8 left-1/2 -translate-x-1/2 sm:left-8 sm:translate-x-0 sm:top-10 lg:left-16 xl:left-24 flex items-center z-10">
        <img src="/logo-white.svg" alt="Open Academy" className="h-8 sm:h-6 w-auto" />
      </div>

      <div className="absolute bottom-6 left-4 sm:left-8 lg:left-16 xl:left-24 z-10">
        <LanguageToggle variant="auth" />
      </div>

      <div className="flex-1 flex flex-col justify-center px-4 sm:px-8 lg:px-16 xl:px-24 pt-16 pb-8 lg:py-0">
        <div className="w-full max-w-[400px] mx-auto">
          {linkSent ? (
            <AuthLinkSentNotice
              email={email}
              onChangeEmail={() => {
                setLinkSent(false)
                setEmail('')
              }}
            />
          ) : (
            <>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-6 sm:mb-8">
                {l.title}
              </h1>
              <AuthEmailForm
                email={email}
                setEmail={setEmail}
                emailError={emailError}
                accountNotFound={accountNotFound}
                isLoading={isLoading}
                isGoogleLoading={isGoogleLoading}
                onSubmit={handleEmailSubmit}
                onGoogle={handleGoogle}
              />
            </>
          )}
        </div>
      </div>

      <AuthIllustration />
    </main>
  )
}
